import { ChevronLeft, MapPin, Phone, TriangleAlert, UserRound } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { EmptyState } from "../components/EmptyState";
import { MeasurementValues } from "../components/MeasurementValues";
import { trpc } from "../lib/trpc";

const kindLabels = {
  residence: "Residence",
  condominium: "Condominio",
  hotel: "Hotel",
  villa: "Villa",
  private_home: "Privato",
  other: "Altro",
} as const;

function quando(data: Date | string) {
  return new Date(data).toLocaleString("it-IT", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Il giorno scritto nel campo data vale tutto il giorno, fino alle 23:59 di quella sera. */
function fineGiornata(dateValue: string) {
  const data = new Date(`${dateValue}T23:59:59`);
  return Number.isNaN(data.getTime()) ? null : data;
}

function inizioGiornata(dateValue: string) {
  const data = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(data.getTime()) ? null : data;
}

/**
 * Scheda di una struttura.
 *
 * Nasce per rispondere a una domanda che prima non aveva un posto: "che cosa e stato misurato
 * qui". Lo storico e in ordine di tempo, dal piu recente, con i valori fuori intervallo in
 * evidenza; il filtro per tipo e per periodo lavora sull'elenco gia caricato, cosi le voci del
 * menu non spariscono mentre si filtra.
 */
export function SitePage() {
  const { id = "" } = useParams();
  const site = trpc.sites.get.useQuery({ id });
  const [tipo, setTipo] = useState("");
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const misurazioni = trpc.measurements.forSite.useQuery({
    siteId: id,
    from: dal ? inizioGiornata(dal) : null,
    to: al ? fineGiornata(al) : null,
    limit: 500,
  });

  // I tipi si ricavano dallo storico stesso: sono i lavori che qui hanno prodotto misurazioni,
  // non tutto il catalogo dell'azienda.
  const tipiDisponibili = useMemo(() => {
    const tipi = new Map<string, string>();
    for (const misura of misurazioni.data ?? []) tipi.set(misura.jobTypeId ?? misura.activityLabel, misura.activityLabel);
    return [...tipi].map(([valore, etichetta]) => ({ valore, etichetta }));
  }, [misurazioni.data]);
  const righe = useMemo(
    () => (misurazioni.data ?? []).filter((misura) => !tipo || (misura.jobTypeId ?? misura.activityLabel) === tipo),
    [misurazioni.data, tipo],
  );

  if (site.isLoading) return <main className="page"><p className="loading-copy">Caricamento struttura...</p></main>;
  if (!site.data) return <main className="page"><p className="form-error">Struttura non trovata.</p></main>;
  const item = site.data;

  return (
    <main className="page">
      <Link className="back-link" to="/strutture"><ChevronLeft size={18} /> Torna alle strutture</Link>
      <header className="detail-hero">
        <div className="detail-hero__main">
          <div><span className="tag">{kindLabels[item.kind]}</span></div>
          <h1>{item.name}</h1>
          <p>{item.notes || "Nessuna nota sulla struttura."}</p>
        </div>
        <dl>
          <div><dt><MapPin size={17} /> Indirizzo</dt><dd>{item.address}</dd></div>
          {item.contactName && <div><dt><UserRound size={17} /> Referente</dt><dd>{item.contactName}</dd></div>}
          {item.contactPhone && <div><dt><Phone size={17} /> Telefono</dt><dd><a href={`tel:${item.contactPhone}`}>{item.contactPhone}</a></dd></div>}
        </dl>
      </header>

      <section className="section-block">
        <div className="section-heading">
          <div><p className="eyebrow">Storico</p><h2>Misurazioni</h2></div>
          <div className="section-heading__actions">
            <label className="inline-field">
              <span>Tipo</span>
              <select value={tipo} onChange={(event) => setTipo(event.target.value)}>
                <option value="">Tutti</option>
                {tipiDisponibili.map((voce) => <option key={voce.valore} value={voce.valore}>{voce.etichetta}</option>)}
              </select>
            </label>
            <label className="inline-field"><span>Dal</span><input type="date" value={dal} onChange={(event) => setDal(event.target.value)} /></label>
            <label className="inline-field"><span>Al</span><input type="date" value={al} onChange={(event) => setAl(event.target.value)} /></label>
          </div>
        </div>

        {misurazioni.isLoading && <p className="loading-copy">Caricamento misurazioni...</p>}
        {!misurazioni.isLoading && righe.length === 0 && (
          <EmptyState title="Nessuna misurazione">
            I valori scritti sulle attività di misurazione di questa struttura compariranno qui, dal più recente.
          </EmptyState>
        )}
        {righe.length > 0 && (
          <ul className="measurement-history">
            {righe.map((misura) => (
              <li className={`measurement-history__row${misura.outOfRange ? " measurement-history__row--fuori" : ""}`} key={misura.id}>
                <div className="measurement-history__when">
                  <strong>{quando(misura.measuredAt)}</strong>
                  <small>{misura.activityLabel}</small>
                </div>
                <div className="measurement-history__values">
                  <MeasurementValues values={misura.values} />
                  {misura.outOfRange && <span className="measurement-history__flag"><TriangleAlert size={14} aria-hidden="true" /> fuori intervallo</span>}
                </div>
                <div className="measurement-history__who">
                  <small>{misura.recordedByName}</small>
                  <Link to={`/interventi/${misura.interventionId}`}>{misura.interventionTitle}</Link>
                  {misura.notes && <small className="measurement-history__notes">{misura.notes}</small>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
